import {
  Input,
  Row,
  Col,
  Avatar,
  Badge,
  Popover,
  Divider,
  Space,
  Tooltip,
  Tag,
} from "antd";
import { SearchOutlined, UserOutlined, CrownOutlined } from "@ant-design/icons";
import {
  Users,
  ShieldCheck,
  LogOut,
  ChevronDown,
  ChevronUp,
  Check,
  MonitorPlay,
  HardDrive,
  Menu as MenuIcon,
  Building2,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  ProLangToggle,
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../global/ProLanguageContext";
import {
  logout,
  updateGlobalSearch,
  selectPage,
  requestServerPost,
  loadUserData,
  reloadBrands,
  setActiveAccountId,
} from "../actions";
import GridButton from "../components/GridButton";
import CDXImage from "./CDXImage";
import Constants from "../data/constants";
import PopupManager from "../managers/PopupManager";
import PermissionManager from "../managers/PermissionManager";
import UserManagementModal from "./popups/usermanagement/UserManagermentModal";
import ProUserProfileModal from "./popups/ProUserProfileModal";

// Set to false to force-hide the account switcher regardless of account count
const SHOW_ACCOUNT_SWITCHER = true;

const MOCK_ACCOUNTS = [
  { id: 1, name: "Acme Corp" },
  { id: 2, name: "Sunrise Media" },
  { id: 3, name: "Pacific Retail" },
];

function CDXSearchBar({
  title,
  onChange = () => {},
  logout,
  userdata,
  selectPage,
  updateGlobalSearch,
  globalSearch,
  selectedPage,
  selectedBrandData,
  selectedBranchData,
  accounts = [],
  requestServerPost,
  loadUserData,
  reloadBrands,
  userMMainToken,
  activeAccountId,
  setActiveAccountId,
  showBrandLogo = true,
  onMenuToggle = null,
}) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { lang } = useProLanguage();
  const tTopbar = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).topbar;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Normalize API accounts to { id, name } shape — ids stored as strings for consistent comparison
  const accountList = accounts.map((a) => ({
    id: String(a.AccountId),
    name: a.AccountNote,
  }));
  const showAccountSwitcher = SHOW_ACCOUNT_SWITCHER && accountList.length > 1;

  const selectAccount = (acc) => {
    const userid = userdata && userdata.user && userdata.user.userid;
    if (!userid) return;
    setActiveAccountId(acc.id);
    localStorage.setItem("active_account_id", String(acc.id));
    setAccountDropdownOpen(false);
    const params = { userid, accountid: acc.id };
    requestServerPost(
      "choose_account",
      params,
      {},
      (response) => {
        console.log("choose_account response", JSON.stringify(response));
        if (response && response.data) {
          loadUserData(response.data);
          if (!selectedPage || selectedPage.id !== Constants.BRAND_PAGE) {
            selectPage(Constants.BRAND_PAGE);
          }
          reloadBrands();
        }
      },
      userMMainToken,
    );
  };

  const onSearchChange = (e) => {
    updateGlobalSearch(e.target.value);
    onChange(e.target.data);
  };

  const getRolesView = (rawRoles) => {
    if (!rawRoles) return "";

    if (!Array.isArray(rawRoles)) {
      return rawRoles;
    }

    if (rawRoles.length === 0) return "";

    //render first role as tag and if more that 1 role, show +x more with tooltip
    if (rawRoles.length === 1) {
      return <Tag>{rawRoles[0]}</Tag>;
    }
    if (rawRoles.length > 1) {
      const firstRole = rawRoles[0];
      const restRoles = rawRoles.slice(1);

      return (
        <div style={{ display: "flex", gap: 0 }}>
          <Tag>{firstRole}</Tag>
          {restRoles.length > 0 && (
            <Tooltip title={restRoles.join(", ")}>
              <Tag color="blue">+{restRoles.length}</Tag>
            </Tooltip>
          )}
        </div>
      );
      // return <span><Badge count={rawRoles.length - 1} ><Tag>{rawRoles[0].rolename}</Tag></Badge></span>
    }
    return "";
  };

  const userSettingModule = () => {
    const listUserPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_USER,
      PermissionManager.PERMISSION_TYPE_LIST,
    );
    const listRolePermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_ROLE,
      PermissionManager.PERMISSION_TYPE_LIST,
    );

    const { user = {} } = userdata;
    const { role = "" } = user;
    const username = userdata.user ? userdata.user.username : "";
    const roleLabel = Array.isArray(role) ? role[0] : role;

    const menuItem = (icon, label, onClick, danger = false) => (
      <div
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 14px",
          cursor: "pointer",
          borderRadius: 8,
          color: danger ? "#ef4444" : "#334155",
          fontSize: 14,
          fontWeight: 500,
          transition: "background 0.12s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = danger ? "#fef2f2" : "#f1f5f9")
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span
          style={{
            display: "flex",
            color: danger ? "#ef4444" : "#6366f1",
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
        {label}
      </div>
    );

    return (
      <div style={{ width: 320, padding: "4px 0" }}>
        {/* User info header */}
        <div
          style={{
            padding: "12px 14px 10px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#6366f1,#4f46e5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserOutlined style={{ color: "#fff", fontSize: 17 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {username}
            </div>
            {roleLabel ? (
              <div
                style={{
                  display: "inline-block",
                  marginTop: 3,
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "1px 7px",
                  borderRadius: 20,
                  background: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
                  color: "#6366f1",
                  letterSpacing: "0.04em",
                }}
              >
                {Array.isArray(role) ? role.join(", ") : role}
              </div>
            ) : null}
          </div>
        </div>

        {/* Account switcher */}
        {showAccountSwitcher && (
          <>
            <div
              style={{ height: 1, background: "#f1f5f9", margin: "2px 0 8px" }}
            />
            <div style={{ padding: "0 14px 6px" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#94a3b8",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                {tTopbar.account}
              </div>
              {/* Dropdown trigger */}
              <div
                onClick={() => setAccountDropdownOpen((o) => !o)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "7px 10px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  background: accountDropdownOpen ? "#f8fafc" : "#fff",
                  transition: "background 0.12s",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}
                >
                  {accountList.find((a) => a.id === activeAccountId)?.name ??
                    tTopbar.selectAccount}
                </span>
                {accountDropdownOpen ? (
                  <ChevronUp size={13} color="#6366f1" />
                ) : (
                  <ChevronDown size={13} color="#94a3b8" />
                )}
              </div>
              {/* Account list */}
              {accountDropdownOpen && (
                <div
                  style={{
                    marginTop: 4,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  {accountList.map((acc) => {
                    const isActive = acc.id === activeAccountId;
                    return (
                      <div
                        key={acc.id}
                        onClick={() => selectAccount(acc)}
                        style={{
                          cursor: "pointer",
                          padding: "8px 12px",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: isActive ? "#eef2ff" : "#fff",
                          borderBottom: "1px solid #f8fafc",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive)
                            e.currentTarget.style.background = "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive)
                            e.currentTarget.style.background = "#fff";
                        }}
                      >
                        <span
                          style={{
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? "#6366f1" : "#334155",
                          }}
                        >
                          {acc.name}
                        </span>
                        {isActive && (
                          <Check size={12} color="#6366f1" strokeWidth={3} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        <div
          style={{
            padding: "4px 12px 8px",
            display: "flex",
            gap: 8,
            flexDirection: "row",
            alignItems: "stretch",
          }}
        >
          <div
            title="Screens used / total"
            style={{
              display: "flex",
              alignItems: "stretch",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: 5,
              padding: "7px 10px",
              borderRadius: 10,
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.18)",
              width: "calc(50% - 4px)",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <MonitorPlay size={14} color="#6366f1" />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                1 / 1
              </span>
            </div>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
                marginLeft: 19,
                lineHeight: 1,
              }}
            >
              {tTopbar.screens}
            </span>
          </div>

          <div
            title="Storage used / total"
            style={{
              display: "flex",
              alignItems: "stretch",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: 5,
              padding: "7px 10px",
              borderRadius: 10,
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.18)",
              width: "calc(50% - 4px)",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <HardDrive size={14} color="#6366f1" />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                424 MB / 100 GB
              </span>
            </div>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
                marginLeft: 19,
                lineHeight: 1,
              }}
            >
              {tTopbar.storage}
            </span>
          </div>
        </div>

        {/* Menu items */}
        <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
        <div style={{ padding: "4px 6px" }}>
          {menuItem(
            <UserOutlined style={{ fontSize: 16 }} />,
            tTopbar.editProfile,
            () => {
              setProfileMenuOpen(false);
              setShowProfileModal(true);
            },
          )}
          {menuItem(
            <Building2 size={16} />,
            tTopbar.workspaceSettings || "Select Workspace",
            () => {
              setProfileMenuOpen(false);
              selectPage(Constants.BRAND_PAGE);
            },
          )}
          {listUserPermission &&
            menuItem(<Users size={16} />, tTopbar.userManagement, () => {
              setProfileMenuOpen(false);
              showUserManagementPopup();
            })}
          {listRolePermission &&
            menuItem(<ShieldCheck size={16} />, tTopbar.roleManagement, () => {
              setProfileMenuOpen(false);
              PopupManager.launchRoleManagementPopup();
            })}
          {menuItem(
            <LogOut size={16} />,
            tTopbar.signOut,
            () => {
              setProfileMenuOpen(false);
              logout();
            },
            true,
          )}
          {/* <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
          {menuItem(<LogOut size={15} />, "Sign Out", logout, true)} */}
        </div>
      </div>
    );
  };

  const showUserManagementPopup = () => {
    // setShowUserManagementModal(true);
    PopupManager.launchUserManagementPopup();
  };
  useEffect(() => {
    // console.log("menuTitle " + title)
  });

  const buttonDimension = "30px";

  // console.log("selectedPage " + JSON.stringify(selectedPage))
  return (
    <Row
      justify="space-around"
      type="flex"
      style={{
        minHeight: "56px",
        width: "100%",
        alignItems: "center",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        flexWrap: "nowrap",
      }}
    >
      {/* Mobile: show hamburger only */}
      {showBrandLogo && isMobile && onMenuToggle && (
        <div
          style={{
            paddingLeft: "8px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            onClick={onMenuToggle}
            style={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "rgba(255,255,255,0.8)",
              flexShrink: 0,
              background: "rgba(255,255,255,0.08)",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.14)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
            }
          >
            <MenuIcon size={18} />
          </div>
        </div>
      )}

      {showBrandLogo && !isMobile ? (
        <div
          id="pro-topbar-workspace"
          style={{
            zIndex: 50,
            left: 0,
            paddingLeft: "8px",
            paddingRight: "16px",
            fontSize: 16,
            color: "rgba(255,255,255,0.95)",
            fontWeight: 400,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {/* Hamburger toggle */}
          {onMenuToggle && (
            <div
              onClick={onMenuToggle}
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
                flexShrink: 0,
                background: "rgba(255,255,255,0.08)",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.14)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
              }
            >
              <MenuIcon size={18} />
            </div>
          )}
          <div
            onClick={() => selectPage("brand")}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              flexShrink: 0,
              overflow: "hidden",
              cursor: "pointer",
            }}
          >
            <img
              src={
                selectedBrandData?.BrandLogo ||
                "/cms/assets/svgs/cdx_logo_blackandwhite.svg"
              }
              style={{
                width: "36px",
                height: "36px",
                objectFit: "cover",
                display: "block",
              }}
              alt={selectedBrandData?.BrandName || "CODEX"}
              onError={(e) => {
                e.currentTarget.src =
                  "/cms/assets/svgs/cdx_logo_blackandwhite.svg";
              }}
            />
          </div>

          {/* Account Name + Location */}
          {selectedBrandData ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.95)",
                  letterSpacing: "0.02em",
                  lineHeight: 1.2,
                }}
              >
                {selectedBrandData.BrandName || "ACC_BF02419B14C2"}
              </span>
              {selectedBranchData?.BranchName && (
                <span
                  onClick={() => selectPage("branch")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    cursor: "pointer",
                  }}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="#ef4444"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle
                      cx="12"
                      cy="10"
                      r="3"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#fca5a5",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {selectedBranchData.BranchName}
                  </span>
                </span>
              )}
            </div>
          ) : null}

          {/* PRO Badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: 700,
              color: "#fbbf24",
              background: "#1e293b",
              border: "1px solid #fbbf24",
              letterSpacing: "0.06em",
            }}
          >
            ✦ PRO
          </span>
        </div>
      ) : null}

      <Col
        className="search-bar-input"
        flex={1}
        style={{
          minWidth: 0,
          height: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: isMobile ? "8px" : "0",
        }}
      >
        <Input
          style={{
            border: "1px solid rgba(255,255,255,0.15)",
            maxWidth: "600px",
            margin: "0px 10px",
            borderRadius: "8px",
            width: "100%",
            height: "36px",
            backgroundColor: "rgba(255,255,255,0.95)",
            fontSize: "14px",
          }}
          placeholder="Search..."
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          // suffix={
          //   <span
          //     style={{
          //       fontSize: "11px",
          //       color: "#94a3b8",
          //       padding: "2px 6px",
          //       borderRadius: "4px",
          //       border: "1px solid #e2e8f0",
          //       backgroundColor: "#f8fafc",
          //       fontWeight: 600,
          //     }}
          //   >
          //     ⌘ K
          //   </span>
          // }
          value={globalSearch}
          onChange={onSearchChange}
        />
      </Col>
      <Col
        style={{
          height: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingRight: "8px",
          flexShrink: 0,
        }}
      >
        <ProLangToggle />
      </Col>
      <Col
        style={{
          height: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Popover
          trigger="click"
          placement="bottomRight"
          title={null}
          visible={profileMenuOpen}
          onVisibleChange={setProfileMenuOpen}
          overlayInnerStyle={{
            padding: 0,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
          content={userSettingModule}
        >
          <Avatar
            style={{
              marginRight: "8px",
              color: "#a5b4fc",
              backgroundColor: "rgba(99,102,241,0.25)",
              cursor: "pointer",
            }}
            size={28}
            icon={<UserOutlined />}
          />
        </Popover>
      </Col>
      {/* <UserManagementModal visible={showUserManagementModal}></UserManagementModal> */}
      <ProUserProfileModal
        visible={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userdata={userdata}
        usertoken={userMMainToken}
        requestServerPost={requestServerPost}
        loadUserData={loadUserData}
      />
    </Row>
  );

  function toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    accounts: state.accounts,
    userMMainToken: state.usermaintoken,
    globalSearch: state.globalSearch,
    selectedPage: state.selectedPage,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
    activeAccountId: state.activeAccountId,
  };
};

export default connect(mapStateToProps, {
  logout,
  selectPage,
  updateGlobalSearch,
  requestServerPost,
  loadUserData,
  reloadBrands,
  setActiveAccountId,
})(CDXSearchBar);
